'use client';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PaymentIntent, PaymentRequest } from '@stripe/stripe-js';

import {
  CARDIOLOGY_BP_MONITORING_NOTE,
  type CardiologyBookingOption,
  type GpBookingOption,
  type PaymentPricingSource,
} from '../components/PaymentDetails/paymentPricing';
import type { RawAvailabilityDay } from '../shared/utils/filterAvailabilities';
import type { BookingDoctor } from '../utils/mapDoctorToBookingDoctor';

export interface BookingWidgetEditState {
  personal: boolean;
  calendar: boolean;
}

export interface BookingFlowTimeState {
  SlotId?: number | string | null;
  slotId?: number | string | null;
  timeAndDate?: number | string;
  time?: string | null;
  Time?: string | null;
  date?: string;
  timestamp?: number | string;
  [key: string]: unknown;
}

export interface BookingFlowPatientDetails {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  dob?: string;
  email?: string | null;
  phone?: string | { country: string; number: string };
  dialCode?: string;
  countryCode?: string;
  patientReceiveEmail?: boolean;
  termAndConditions?: boolean;
  [key: string]: unknown;
}

export interface BookingWidgetApplePayState {
  paymentIntent?: PaymentIntent | null;
  clientSecret?: string | null;
  paymentRequest?: PaymentRequest | null;
}

export type SearchAheadStatus = 'idle' | 'searching' | 'exhausted' | 'failed';

export interface BookingFlowStoreState {
  selectedDoctor: BookingDoctor | null;
  setSelectedDoctor: (doctor: BookingDoctor | null) => void;
  appleAndGooglePayData: BookingWidgetApplePayState | null;
  setAppleAndGooglePayData: (data: BookingWidgetApplePayState | null) => void;

  currentStep: number;
  setCurrentStep: (step: number) => void;

  selectedTime: BookingFlowTimeState | null;
  setSelectedTime: (time: BookingFlowTimeState | null) => void;

  patientDetails: BookingFlowPatientDetails | null;
  setPatientDetails: (details: BookingFlowPatientDetails | null) => void;

  paymentDetails: PaymentPricingSource | null;
  setPaymentDetails: (details: PaymentPricingSource | null) => void;

  availabilities: RawAvailabilityDay[] | null;
  setAvailabilities: (availabilities: unknown[] | null) => void;

  availabilitiesLoader: boolean;
  setAvailabilitiesLoader: (loader: boolean) => void;

  isRateLimited: boolean;
  setRateLimited: (limited: boolean) => void;

  availabilityError: string | null;
  setAvailabilityError: (error: string | null) => void;

  searchAheadStatus: SearchAheadStatus;
  setSearchAheadStatus: (status: SearchAheadStatus) => void;

  startPrinting: boolean;
  setStartPrinting: (printing: boolean) => void;
  showModal: boolean;
  setShowModal: (modal: boolean) => void;
  isBookingWidgetEdit: BookingWidgetEditState;
  setIsBookingWidgetEdit: (mode: BookingWidgetEditState) => void;
  setPersonalEdit: (value: boolean) => void;
  setCalendarEdit: (value: boolean) => void;

  // NEW: Widget visibility state
  isWidgetOpen: boolean;
  setWidgetOpen: (isOpen: boolean) => void;
  closeWidget: () => void;
  toggleWidget: () => void;

  // Package booking context
  notes: string;
  setNotes: (notes: string) => void;
  // Package-level deposit, set via handleBook from package data.
  // Independent from selectedDoctor/selectedClinic.depositFee (existing dormant deposit system).
  depositFee: number | null;
  setDepositFee: (fee: number | null) => void;
  // Package-level total price, set via handleBook from package data.
  // When set, getPaymentPricing uses this instead of selectedDoctor.primaryPrice.
  packagePrice: number | null;
  setPackagePrice: (price: number | null) => void;

  // User choice at payment step: 'full' (pay total) or 'deposit' (pay deposit today).
  // Only meaningful when hasDeposit is true in paymentPricing. Defaults 'full'.
  paymentChoice: 'full' | 'deposit';
  setPaymentChoice: (choice: 'full' | 'deposit') => void;

  // GP booking type choice (categoryId 58): either a standard GP consultation
  // at the doctor's primaryPrice or a Pathology Review at a fixed £95.
  gpBookingOption: GpBookingOption;
  setGpBookingOption: (option: GpBookingOption) => void;

  // Authoritative order total resolved at the payment step (getPaymentPricing
  // totalAmount = total appointment/package value, deposit-only doctors use
  // their deposit as the full amount). Used ONLY for purchase/conversion
  // analytics. This is NOT the Stripe charge (chargeAmountToday) and must
  // never be sent to the payment backend. Session-scoped: deliberately
  // excluded from the persisted partialize payload.
  bookingTotalValue: number | null;
  setBookingTotalValue: (amount: number | null) => void;
  cardiologyBookingOption: CardiologyBookingOption;
  setCardiologyBookingOption: (option: CardiologyBookingOption) => void;
}

export const useBookingFlowStore = create<BookingFlowStoreState>()(
  persist(
    (set, get) => ({
      selectedDoctor: null,
      setSelectedDoctor: (doctor) =>
        set({
          selectedDoctor: doctor,
          cardiologyBookingOption: 'consultation',
          // Package consumers set these AFTER selecting the doctor. Starting
          // any new doctor context must not inherit a previous package's notes
          // or pricing (e.g. stale Baby Hip Clinic notes on Claudia's profile).
          notes: '',
          depositFee: null,
          packagePrice: null,
        }),
      appleAndGooglePayData: null,
      setAppleAndGooglePayData: (data) => set({ appleAndGooglePayData: data }),

      currentStep: 1,
      setCurrentStep: (step) => set({ currentStep: step }),

      selectedTime: null,
      setSelectedTime: (time) => set({ selectedTime: time }),

      patientDetails: null,
      setPatientDetails: (details) => set({ patientDetails: details }),

      paymentDetails: null,
      setPaymentDetails: (details) => set({ paymentDetails: details }),

      availabilities: null,
      setAvailabilities: (availabilities) =>
        set({
          availabilities: availabilities as RawAvailabilityDay[] | null,
        }),

      availabilitiesLoader: false,
      setAvailabilitiesLoader: (loader) =>
        set({ availabilitiesLoader: loader }),

      isRateLimited: false,
      setRateLimited: (limited) => set({ isRateLimited: limited }),

      availabilityError: null,
      setAvailabilityError: (error) => set({ availabilityError: error }),

      searchAheadStatus: 'idle',
      setSearchAheadStatus: (status) => set({ searchAheadStatus: status }),

      startPrinting: false,
      setStartPrinting: (printing) => set({ startPrinting: printing }),
      showModal: false,
      setShowModal: (modal) => set({ showModal: modal }),
      isBookingWidgetEdit: { personal: false, calendar: false },
      setIsBookingWidgetEdit: (mode) => set({ isBookingWidgetEdit: mode }),
      setPersonalEdit: (value) =>
        set((state) => ({
          isBookingWidgetEdit: {
            ...state.isBookingWidgetEdit,
            personal: value,
          },
        })),
      setCalendarEdit: (value) =>
        set((state) => ({
          isBookingWidgetEdit: {
            ...state.isBookingWidgetEdit,
            calendar: value,
          },
        })),

      // NEW: Widget visibility state
      isWidgetOpen: false,
      setWidgetOpen: (isOpen) => {
        set({ isWidgetOpen: isOpen });
        // Prevent/restore body scroll when widget is open
        if (typeof document !== 'undefined') {
          document.body.style.overflow = isOpen ? 'hidden' : '';
        }
      },
      closeWidget: () => {
        set({ isWidgetOpen: false });
        if (typeof document !== 'undefined') {
          document.body.style.overflow = '';
        }
      },
      toggleWidget: () => {
        const { isWidgetOpen, setWidgetOpen } = get();
        setWidgetOpen(!isWidgetOpen);
      },

      // Package booking context
      notes: '',
      setNotes: (notes) => set({ notes }),
      depositFee: null,
      setDepositFee: (fee) => set({ depositFee: fee }),
      packagePrice: null,
      setPackagePrice: (price) => set({ packagePrice: price }),
      paymentChoice: 'full',
      setPaymentChoice: (choice) => set({ paymentChoice: choice }),
      gpBookingOption: 'consultation',
      setGpBookingOption: (option) => set({ gpBookingOption: option }),
      bookingTotalValue: null,
      setBookingTotalValue: (amount) => set({ bookingTotalValue: amount }),
      cardiologyBookingOption: 'consultation',
      setCardiologyBookingOption: (option) =>
        set({
          cardiologyBookingOption: option,
          notes:
            option === 'bp-monitoring' ? CARDIOLOGY_BP_MONITORING_NOTE : '',
        }),
    }),
    {
      name: 'booking-flow-storage',
      partialize: (state) => ({
        currentStep: state.currentStep,
        selectedDoctor: state.selectedDoctor,
        // DON'T persist isWidgetOpen - it should reset on page refresh
        // isWidgetOpen: state.isWidgetOpen, // ← Don't include this
      }),
    },
  ),
);
